import { api } from './api'
import { getAll as get } from './pagination'

export interface BookCategory { id: number; name: string; description?: string | null; color: string }
export interface Author { id: number; first_name: string; last_name: string; middle_name?: string | null; full_name: string; nationality?: string; biography?: string | null }
export interface Book {
  id: number
  title: string
  subtitle?: string | null
  isbn?: string | null
  authors: number[]
  authors_display: string
  publisher?: number | null
  publisher_name?: string | null
  category?: number | null
  category_name?: string | null
  edition?: string | null
  publication_year?: number | null
  pages?: number | null
  language?: string
  description?: string | null
  shelf_number?: string | null
  row_number?: string | null
  position?: string | null
  acquisition_date?: string | null
  acquisition_price?: string | null
  source?: string | null
  tags?: string | null
  notes?: string | null
  status: string
  available_copies: number
  total_copies: number
  is_available: boolean
  created_at?: string
}
export interface BookCopy { id: number; book: number; book_title: string; book_authors?: string; copy_number: string; barcode?: string | null; condition: string; status: string; location?: string | null; notes?: string | null }
export interface Borrowing {
  id: number
  book_copy: number
  book_title: string
  book_authors?: string
  borrower: number
  borrower_name: string
  borrowed_by_name?: string
  returned_by_name?: string | null
  borrow_date: string
  due_date: string
  return_date?: string | null
  status: 'active' | 'returned' | 'overdue' | 'lost' | 'damaged'
  fine_amount: string
  fine_paid: boolean
  notes?: string | null
  renewal_count: number
  is_overdue: boolean
  days_overdue: number
}
export interface Reservation { id: number; book: number; book_title: string; book_authors?: string; student: number; student_name: string; reserved_by_name?: string; reservation_date: string; expiry_date: string; fulfillment_date?: string | null; status: 'pending' | 'active' | 'fulfilled' | 'cancelled' | 'expired'; priority: number; notes?: string | null }
export interface Fine {
  id: number
  borrowing: number
  book_title: string
  student: number
  student_name: string
  fine_type: 'overdue' | 'damage' | 'loss' | 'other'
  amount: string
  reason: string
  status: 'pending' | 'paid' | 'waived' | 'cancelled'
  issued_by_name?: string
  issued_date: string
  paid_date?: string | null
  waived_by_name?: string | null
  waived_date?: string | null
  payment_reference?: string | null
  waiver_reason?: string | null
}
export interface LibraryStats { total_books: number; total_copies: number; available_books: number; active_borrowings: number; overdue_books: number; pending_fines: string | number }
export interface EligibleStudent { id: number; full_name: string; student_id: string; current_class_name?: string | null }
export interface ReadingProgram { id: number; name: string; description: string; program_type: string; start_date: string; end_date: string; target_books?: number | null; target_pages?: number | null; is_active: boolean; participant_count: number }
export interface ReadingProgress { id: number; student: number; student_name: string; program: number; program_name: string; books_read: number; pages_read: number; minutes_read: number; last_activity?: string | null; is_completed: boolean; completion_date?: string | null }

export type BookInput = Omit<Partial<Book>, 'acquisition_price'> & { acquisition_price?: string | null }

export const libraryService = {
  getCategories: () => get<BookCategory>('/library/categories/'),
  createCategory: async (data: Partial<BookCategory>) => (await api.post('/library/categories/', data)).data as BookCategory,
  updateCategory: async (id: number, data: Partial<BookCategory>) => (await api.patch(`/library/categories/${id}/`, data)).data as BookCategory,
  deleteCategory: async (id: number) => { await api.delete(`/library/categories/${id}/`) },
  getAuthors: () => get<Author>('/library/authors/'),
  createAuthor: async (data: Partial<Author>) => (await api.post('/library/authors/', data)).data as Author,
  updateAuthor: async (id: number, data: Partial<Author>) => (await api.patch(`/library/authors/${id}/`, data)).data as Author,
  deleteAuthor: async (id: number) => { await api.delete(`/library/authors/${id}/`) },
  getBooks: () => get<Book>('/library/books/'),
  createBook: async (data: BookInput) => (await api.post('/library/books/', data)).data as Book,
  updateBook: async (id: number, data: BookInput) => (await api.patch(`/library/books/${id}/`, data)).data as Book,
  /** Refused while the title still has copies. */
  deleteBook: async (id: number) => { await api.delete(`/library/books/${id}/`) },
  getCopies: () => get<BookCopy>('/library/copies/'),
  createCopy: async (data: Partial<BookCopy>) => (await api.post('/library/copies/', data)).data as BookCopy,
  updateCopy: async (id: number, data: Partial<BookCopy>) => (await api.patch(`/library/copies/${id}/`, data)).data as BookCopy,
  /** Refused once the copy has been lent. */
  deleteCopy: async (id: number) => { await api.delete(`/library/copies/${id}/`) },
  getBorrowings: () => get<Borrowing>('/library/borrowings/'),
  borrow: async (data: { book_copy_id: number; student_id: number; due_date?: string }) => (await api.post('/library/borrow/', data)).data as Borrowing,
  returnBook: async (data: { borrowing_id: number; condition: string; damage_fine?: string }) => (await api.post('/library/return/', data)).data as Borrowing,
  renew: async (borrowing_id: number) => (await api.post('/library/renew/', { borrowing_id })).data as Borrowing,
  reportLost: async (data: { borrowing_id: number; replacement_cost: string }) => (await api.post('/library/report-lost/', data)).data as Borrowing,
  getReservations: () => get<Reservation>('/library/reservations/'),
  reserve: async (data: { book_id: number; student_id: number; expiry_days?: number }) => (await api.post('/library/reserve/', data)).data as Reservation,
  cancelReservation: async (id: number) => (await api.post(`/library/reservations/${id}/cancel/`)).data as Reservation,
  fulfillReservation: async (id: number) => (await api.post(`/library/reservations/${id}/fulfill/`)).data as Borrowing,
  getFines: () => get<Fine>('/library/fines/'),
  payFine: async (id: number, payment_reference: string) => (await api.post(`/library/fines/${id}/pay/`, { payment_reference })).data as Fine,
  waiveFine: async (id: number, reason: string) => (await api.post(`/library/fines/${id}/waive/`, { reason })).data as Fine,
  getReadingPrograms: () => get<ReadingProgram>('/library/reading-programs/'),
  createReadingProgram: async (data: Partial<ReadingProgram>) => (await api.post('/library/reading-programs/', data)).data as ReadingProgram,
  updateReadingProgram: async (id: number, data: Partial<ReadingProgram>) => (await api.patch(`/library/reading-programs/${id}/`, data)).data as ReadingProgram,
  getReadingProgress: () => get<ReadingProgress>('/library/reading-progress/'),
  enrollReadingProgram: async (programId: number, studentId: number) => (await api.post(`/library/reading-programs/${programId}/enroll/`, { student_id: studentId })).data,
  updateReadingProgress: async (id: number, data: { books_read: number; pages_read: number; minutes_read: number }) => (await api.post(`/library/reading-progress/${id}/update/`, data)).data,
  getStudents: async () => (await api.get('/library/eligible-students/')).data as EligibleStudent[],
  getStats: async () => (await api.get('/library/statistics/')).data as LibraryStats,
}
